import { _TypedDataEncoder } from "@ethersproject/hash"
import {
  AppError,
  CommonErrorsKey,
  MaxSigDeadline,
  type PermitBatch,
  type PermitSingle,
  getBatchPermit,
  getSinglePermit,
  isSupportedChainId,
  permit2Abi,
} from "@eulerxyz/core-sdk"
import type { Config } from "@wagmi/core"
import type {
  Address,
  BaseErrorType,
  Client,
  SignMessageErrorType,
  SignTypedDataReturnType,
} from "viem"
import { http, createPublicClient, publicActions } from "viem"
import type { Connector } from "wagmi"
import type { GetConnectorClientErrorType } from "wagmi/actions"
import { getConnectorClient } from "wagmi/actions"
import type { ErrorType, GetAccountParameter, UnionEvaluate } from "../types"

type Details = {
  amount: string | number | bigint
  expiration: number
  token: Address
}

type Permit2ParamtersBase = {
  spender: Address
  sigDeadline?: string | number | bigint
  permit2Address: Address
  connector?: Connector
} & GetAccountParameter

type Permit2ParametersSingle = Permit2ParamtersBase & {
  details: Details // Single, not an array
}

type Permit2ParametersBatch = Permit2ParamtersBase & {
  details: Details[] // Array of Details
}

export type Permit2Parameters = UnionEvaluate<
  Permit2ParametersSingle | Permit2ParametersBatch
>

// Type guard for single parameters
function isPermit2ParametersSingle(
  parameters: Permit2Parameters,
): parameters is Permit2ParametersSingle {
  return !Array.isArray(parameters.details)
}

export type Permit2ReturnType = {
  signature: SignTypedDataReturnType
  permit: PermitSingle | PermitBatch
}

export type Permit2ErrorType =
  | GetConnectorClientErrorType
  // base
  | BaseErrorType
  | ErrorType
  // viem
  | SignMessageErrorType
  // Custom
  | AppError

async function resolveClient(
  config: Config,
  parameters: Permit2Parameters,
): Promise<Client> {
  const { account, connector } = parameters

  if (typeof account === "object" && account.type === "local") {
    return config.getClient()
  }
  return getConnectorClient(config, { account, connector })
}

async function getNonce(
  client: Client,
  token: Address,
  owner: Address,
  spender: Address,
  permit2Address: Address,
) {
  // Fix: Coinbase Wallet needs to use a public client because its connector provider attempts to make a
  // call with CORS enabled and adds extra parameters (client-project-name) to the RPC request. This causes issues and revert the request.
  const publicClient = createPublicClient({
    chain: client.chain,
    transport: http(),
  })
  const [, , nonce] = await publicClient.extend(publicActions).readContract({
    address: permit2Address,
    abi: permit2Abi,
    functionName: "allowance",
    args: [owner, token, spender],
  })
  return nonce
}

async function handleSinglePermit(
  client: Client,
  owner: Address,
  parameters: Permit2ParametersSingle,
) {
  if (!isSupportedChainId(client.chain?.id || -1))
    throw new AppError(CommonErrorsKey.ChainNotSupported, "No chain found")

  const {
    details,
    spender,
    sigDeadline = MaxSigDeadline,
    permit2Address,
  } = parameters
  const nonce = await getNonce(
    client,
    details.token,
    owner,
    spender,
    permit2Address,
  )
  const permitSingle: PermitSingle = {
    details: { ...details, amount: BigInt(details.amount), nonce },
    sigDeadline: BigInt(sigDeadline),
    spender,
  }
  const permitSignature = getSinglePermit(
    permitSingle,
    client.chain?.id || 1,
    permit2Address,
  )
  return { ...permitSignature, permit: permitSingle, account: owner }
}

async function handleBatchPermits(
  client: Client,
  owner: Address,
  parameters: Permit2ParametersBatch,
) {
  if (!client.chain)
    throw new AppError(CommonErrorsKey.ChainNotSupported, "No chain found")
  const {
    details,
    spender,
    sigDeadline = MaxSigDeadline,
    permit2Address,
  } = parameters
  const batchDetails = await Promise.all(
    details.map(async (detail) => {
      const nonce = await getNonce(
        client,
        detail.token,
        owner,
        spender,
        permit2Address,
      )
      return { ...detail, amount: BigInt(detail.amount), nonce }
    }),
  )
  const permitBatch: PermitBatch = {
    details: batchDetails,
    spender,
    sigDeadline: BigInt(sigDeadline),
  }
  const permitSignature = getBatchPermit(
    permitBatch,
    client.chain.id,
    permit2Address,
  )
  return { ...permitSignature, permit: permitBatch, account: owner }
}

export async function permit2(
  config: Config,
  parameters: Permit2Parameters,
): Promise<Permit2ReturnType> {
  const client = await resolveClient(config, parameters)
  if (!client.account)
    throw new AppError(CommonErrorsKey.AccountNotFound, "No account found")

  if (isPermit2ParametersSingle(parameters)) {
    const single = await handleSinglePermit(
      client,
      client.account.address,
      parameters,
    )

    if (!single.domain || !single.types)
      throw new AppError(CommonErrorsKey.Permit2Failed, "No domain found")

    const message = JSON.stringify(
      _TypedDataEncoder.getPayload(single.domain, single.types, single.message),
    )
    return client
      .request({
        method: "eth_signTypedData_v4",
        params: [client.account.address, message],
      })
      .then((signature) => ({
        signature,
        permit: single.permit,
      }))
  }

  const batch = await handleBatchPermits(
    client,
    client.account.address,
    parameters,
  )
  const message = JSON.stringify(
    _TypedDataEncoder.getPayload(batch.domain, batch.types, batch.message),
  )
  return client
    .request({
      method: "eth_signTypedData_v4",
      params: [client.account.address, message],
    })
    .then((signature) => ({
      signature,
      permit: batch.permit,
    }))
}
